import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { classifyVideoUrl, describeVideoSource } from '../utils/videoEmbed';
import SmartVideo from '../components/SmartVideo';
import { cn } from '../lib/utils';
import {
  ImageIcon, Video, Music, Upload, Plus, Trash2, Pencil, ExternalLink,
  FileVideo, FileImage, FileAudio, Globe, Link2, Eye, FileText, File,
  FileSpreadsheet, FileCode, Archive
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const MEDIA_TYPES = [
  { value: 'image', label: 'Images', icon: ImageIcon, color: 'bg-blue-100 text-blue-700' },
  { value: 'video', label: 'Videos', icon: Video, color: 'bg-purple-100 text-purple-700' },
  { value: 'music', label: 'Audio', icon: Music, color: 'bg-amber-100 text-amber-700' },
  { value: 'document', label: 'Documents', icon: FileText, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'archive', label: 'Archives', icon: Archive, color: 'bg-slate-100 text-slate-700' },
  { value: 'other', label: 'Other', icon: File, color: 'bg-gray-100 text-gray-700' },
];

const ASSIGNMENTS = [
  { value: '', label: 'Unassigned' },
  { value: 'landing_hero_video', label: 'Landing Page - Hero Video' },
  { value: 'landing_testimonial', label: 'Landing Page - Testimonial' },
  { value: 'onboarding_video', label: 'Onboarding Tutorial' },
  { value: 'email_header', label: 'Email Header Image' },
  { value: 'proposal_cover', label: 'Proposal Cover Image' },
  { value: 'proposal_attachment', label: 'Proposal Attachment' },
  { value: 'brand_logo', label: 'Brand Logo' },
  { value: 'social_media', label: 'Social Media Content' },
  { value: 'knowledge_base', label: 'Knowledge Base' },
  { value: 'contract_template', label: 'Contract Template' },
  { value: 'other', label: 'Other' },
];

// File type to icon mapping
const FILE_ICONS = {
  'pdf': { icon: FileText, color: 'text-red-500' },
  'doc': { icon: FileText, color: 'text-blue-500' },
  'docx': { icon: FileText, color: 'text-blue-500' },
  'xls': { icon: FileSpreadsheet, color: 'text-green-600' },
  'xlsx': { icon: FileSpreadsheet, color: 'text-green-600' },
  'ppt': { icon: FileImage, color: 'text-orange-500' },
  'pptx': { icon: FileImage, color: 'text-orange-500' },
  'txt': { icon: FileText, color: 'text-gray-500' },
  'csv': { icon: FileSpreadsheet, color: 'text-green-500' },
  'zip': { icon: Archive, color: 'text-purple-500' },
  'rar': { icon: Archive, color: 'text-purple-500' },
  '7z': { icon: Archive, color: 'text-purple-500' },
  'json': { icon: FileCode, color: 'text-yellow-600' },
  'xml': { icon: FileCode, color: 'text-orange-600' },
  'md': { icon: FileText, color: 'text-blue-400' },
};

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(fileName, mimeType) {
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  return FILE_ICONS[ext] || { icon: File, color: 'text-gray-400' };
}

function UploadDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ title: '', media_type: 'document', description: '', assignment: '', external_url: '' });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    // Client-side size limits — mirror backend FILE_SIZE_LIMITS (v2.5.917)
    const sizeLimits = {
      image:     50 * 1024 * 1024,
      video:    500 * 1024 * 1024,
      music:    200 * 1024 * 1024,
      document: 100 * 1024 * 1024,
      archive:  200 * 1024 * 1024,
      other:     50 * 1024 * 1024,
    };
    
    setFile(f);
    setForm(p => ({ ...p, title: p.title || f.name.replace(/\.[^.]+$/, '') }));

    // Auto-detect type from MIME
    let detectedType = 'document';
    if (f.type.startsWith('image/')) detectedType = 'image';
    else if (f.type.startsWith('video/')) detectedType = 'video';
    else if (f.type.startsWith('audio/')) detectedType = 'music';
    else if (f.type.includes('pdf') || f.type.includes('word') || f.type.includes('excel') || 
             f.type.includes('powerpoint') || f.type.includes('text') || f.type.includes('csv')) {
      detectedType = 'document';
    } else if (f.type.includes('zip') || f.type.includes('rar') || f.type.includes('compressed')) {
      detectedType = 'archive';
    } else {
      detectedType = 'other';
    }
    
    setForm(p => ({ ...p, media_type: detectedType }));

    // Check size after type detection
    const maxSize = sizeLimits[detectedType] || sizeLimits.other;
    if (f.size > maxSize) {
      toast.error(`Max file size for ${detectedType} is ${maxSize / (1024 * 1024)}MB`);
      setFile(null);
      return;
    }

    // Preview for images only
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!file && !form.external_url) { toast.error('Upload a file or provide a URL'); return; }

    setUploading(true);
    setUploadProgress(0);
    try {
      if (file) {
        // v2.5.917 — Always use multipart/form-data streaming. Base64-in-JSON
        // was the reason 44MB video uploads failed on prod: JSON parse of a
        // 59MB base64 string chokes Passenger, and Plesk's nginx buffers the
        // whole body before proxying = OOM. Multipart streams to disk chunk-
        // by-chunk so we can push 500MB videos on cheap hosting.
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', form.title);
        fd.append('media_type', form.media_type);
        fd.append('description', form.description || '');
        fd.append('assignment', form.assignment || '');
        await axios.post(`${API}/api/media/upload-file`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Long timeout — big videos on slow connections can take minutes
          timeout: 30 * 60 * 1000,
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round((evt.loaded * 100) / evt.total);
              setUploadProgress(pct);
            }
          },
        });
      } else {
        // External URL only — no file to stream. Use the classic JSON endpoint.
        await axios.post(`${API}/api/media`, {
          ...form,
          external_url: form.external_url,
        });
      }
      toast.success('Media uploaded successfully');
      onDone();
      onClose();
      setForm({ title: '', media_type: 'document', description: '', assignment: '', external_url: '' });
      setFile(null);
      setPreview(null);
      setUploadProgress(0);
    } catch (err) {
      const detail = err.response?.data?.detail
        || (err.code === 'ECONNABORTED' ? 'Upload timed out — check your network or try a smaller file'
        :   err.message === 'Network Error' ? 'Network error — the server may have hit a body-size limit'
        :   'Upload failed');
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const getFileDisplay = () => {
    if (!file) return null;
    const fileIcon = getFileIcon(file.name, file.type);
    const IconComponent = fileIcon.icon;
    
    return (
      <div className="flex items-center justify-center gap-2">
        <IconComponent className={cn("h-8 w-8", fileIcon.color)} />
        <div className="text-left">
          <p className="text-sm font-medium truncate max-w-[200px]">{file.name}</p>
          <p className="text-xs text-gray-400">{formatSize(file.size)}</p>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="upload-media-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-blue-500" /> Upload Media</DialogTitle>
          <DialogDescription>Upload images, videos, audio, documents, and more</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {/* File Drop */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => fileRef.current?.click()}>
            <input ref={fileRef} type="file" 
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z" 
              className="hidden" onChange={handleFile} />
            {preview ? (
              <img src={preview} alt="Preview" className="w-full max-h-32 object-contain rounded" />
            ) : file ? (
              getFileDisplay()
            ) : (
              <div>
                <Upload className="h-8 w-8 text-gray-300 mx-auto mb-1" />
                <p className="text-sm text-gray-500">Click to upload file</p>
                <p className="text-xs text-gray-400 mt-1">Images, videos, documents, archives, etc.</p>
              </div>
            )}
          </div>

          <div className="text-center text-xs text-gray-400">— OR —</div>
          <div>
            <Label className="text-xs">Video / File URL (Google Drive, YouTube, Vimeo, direct MP4)</Label>
            <Input
              value={form.external_url}
              onChange={e => setForm(p => ({ ...p, external_url: e.target.value }))}
              placeholder="https://drive.google.com/file/d/... or https://youtu.be/..."
              className="mt-1"
              data-testid="media-external-url"
            />
            {/* v2.5.917 — live URL classifier feedback + inline preview so the
                user can see EXACTLY how their pasted link will embed before saving. */}
            {form.external_url && !file && (() => {
              const info = classifyVideoUrl(form.external_url);
              if (info.kind === 'unknown') return null;
              return (
                <div className="mt-2 space-y-1">
                  <div className="text-[11px] flex items-center gap-1.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${info.kind === 'gdrive' ? 'bg-emerald-500' : info.kind === 'youtube' ? 'bg-red-500' : info.kind === 'vimeo' ? 'bg-sky-500' : 'bg-gray-400'}`} />
                    <span className="text-gray-700 font-medium capitalize">{info.kind}</span>
                    <span className="text-gray-500">— {describeVideoSource(info.kind)}</span>
                  </div>
                  <div className="w-full aspect-video border rounded overflow-hidden bg-black" data-testid="media-external-url-preview">
                    <SmartVideo url={form.external_url} title="Preview" autoPlay={false} muted controls className="w-full h-full" />
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="media-title" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.media_type} onValueChange={v => setForm(p => ({ ...p, media_type: v }))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{MEDIA_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Assign To</Label>
            <Select value={form.assignment} onValueChange={v => setForm(p => ({ ...p, assignment: v }))}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Select location..." /></SelectTrigger>
              <SelectContent>{ASSIGNMENTS.map(a => <SelectItem key={a.value || 'none'} value={a.value || 'none'}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} />
          </div>
        </div>
        {/* v2.5.917 — Upload progress bar for streaming multipart uploads */}
        {uploading && file && (
          <div className="px-1 pb-1">
            <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
              <span>Uploading {file.name}...</span>
              <span data-testid="upload-progress-pct">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-150" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleUpload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-media-btn">
            {uploading ? (uploadProgress > 0 && uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Uploading...') : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MediaCard({ item, onDelete, onEdit }) {
  const TypeIcon = MEDIA_TYPES.find(t => t.value === item.media_type)?.icon || File;
  const typeColor = MEDIA_TYPES.find(t => t.value === item.media_type)?.color || 'bg-gray-100 text-gray-700';
  const assignLabel = ASSIGNMENTS.find(a => a.value === item.assignment)?.label || '';
  const url = item.external_url || (item.file_url ? `${API}${item.file_url}` : '');
  
  const fileIcon = getFileIcon(item.file_name, item.mime_type);
  const FileIconComponent = fileIcon.icon;

  const renderThumbnail = () => {
    if (item.media_type === 'image' && url) {
      return <img src={url} alt={item.title} className="w-full h-full object-cover" />;
    }
    
    if (item.media_type === 'document' && item.file_name) {
      return (
        <div className="flex flex-col items-center gap-1">
          <FileIconComponent className={cn("h-10 w-10", fileIcon.color)} />
          <span className="text-[9px] text-gray-400 uppercase">{item.file_name.split('.').pop()}</span>
        </div>
      );
    }
    
    if (item.media_type === 'video' && item.external_url) {
      return (
        <div className="flex flex-col items-center gap-1">
          <Video className="h-8 w-8 text-purple-400" />
          <span className="text-[9px] text-gray-400">External video</span>
        </div>
      );
    }
    
    return <TypeIcon className={cn("h-10 w-10", typeColor.split(' ')[1])} />;
  };

  return (
    <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid={`media-card-${item.id}`}>
      <CardContent className="p-3">
        {/* Thumbnail */}
        <div className="w-full h-32 rounded-lg bg-gray-100 flex items-center justify-center mb-2 overflow-hidden">
          {renderThumbnail()}
        </div>

        <h4 className="text-sm font-semibold truncate" title={item.title}>{item.title}</h4>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <Badge className={cn("text-[9px]", typeColor)}>{item.media_type}</Badge>
          {assignLabel && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">{assignLabel}</Badge>}
          {item.file_size > 0 && <span className="text-[9px] text-gray-400">{formatSize(item.file_size)}</span>}
        </div>
        {item.description && <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">{item.description}</p>}

        <div className="flex items-center gap-1 mt-2">
          {url && (
            <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => window.open(url, '_blank')}>
              <Eye className="h-2.5 w-2.5" /> View
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-400 hover:text-red-600 ml-auto" onClick={() => onDelete(item.id)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MediaManagementPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.media_type = filter;
      const { data } = await axios.get(`${API}/api/media`, { params });
      setItems(data);
    } catch { toast.error('Failed to load media'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media?')) return;
    try {
      await axios.delete(`${API}/api/media/${id}`);
      setItems(prev => prev.filter(m => m.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const counts = { all: items.length, image: 0, video: 0, music: 0, document: 0, archive: 0, other: 0 };
  items.forEach(m => { if (counts[m.media_type] !== undefined) counts[m.media_type]++; });

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="media-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-purple-500" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Media Management<PageInfoTooltip pageKey="media-management" /></h1>
            <p className="text-xs text-gray-400">{items.length} assets · Upload and manage all file types</p>
          </div>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-purple-600 hover:bg-purple-700 gap-1.5" data-testid="upload-media-open">
          <Upload className="h-4 w-4" /> Upload Media
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[{ v: 'all', l: 'All' }, ...MEDIA_TYPES.map(t => ({ v: t.value, l: t.label }))].map(f => (
          <Button key={f.v} size="sm" variant={filter === f.v ? 'default' : 'outline'}
            className={cn("h-7 text-xs gap-1", filter === f.v && 'bg-purple-600 hover:bg-purple-700')}
            onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}>
            {f.l} ({counts[f.v] || 0})
          </Button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No media yet</h3>
            <p className="text-sm text-gray-400 mb-4">Upload images, videos, documents, and more to use across Konnect</p>
            <Button onClick={() => setUploadOpen(true)} className="bg-purple-600 hover:bg-purple-700 gap-1.5"><Upload className="h-4 w-4" /> Upload First Media</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(item => (
            <MediaCard key={item.id} item={item} onDelete={handleDelete} />
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onDone={fetchData} />
    </div>
  );
}
